/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import individualWorkSupportUtil from '../../../../share/util/individualWorkSupportUtil';
import session from '../../../../share/session';
import { REQUEST_STATUS_COMPLETED, REQUEST_STATUS_FAILED } from '../../../../../../share/src/util/api/polling';
import individualDiagnose from '../../../../share/util/individualDiagnose';
import { apiErrorDefaultAction } from '../../../../share/util/api/errorAction';

/** 各フェーズ定義 */
const PHASE = {
  /** 待機 */
  START: 'start',
  /** 実行 */
  EXECUTION: 'execution',
  /** モバイルネットワーク接続設定 ON/OFF */
  SET_MOBILE_NETWORK: 'setMobileNetwork',
  /** 停止 */
  SUCCESS: 'success',
  /** エラー */
  SET_MOBILE_NETWORK_FAILED: 'setMobileNetworkFailed',
  /** 共通エラー */
  FAILED: 'failed'
};

/** API type定義 */
const API_TYPE = {
  READ: 'read',
  WRITE: 'write',
  EXIT: 'exit',
  DISPOSE: 'dispose'
};

export default {
  name: 'requirement157',
  data() {
    return {
      phase: PHASE.START,
      isLoading: false,
      systemId: '',
      modal_requirement_157: false,
      isDispose: true,
      isOff: false,
    };
  },

  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, default: '', required: true },
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId
    }),

    /** ダイアログのヘッダー色 */
    headerColor() {
      switch (this.phase) {
        case PHASE.FAILED:
        case PHASE.SET_MOBILE_NETWORK_FAILED:
          return 'danger';
        case PHASE.SUCCESS:
          return 'success';
        case PHASE.START:
        case PHASE.EXECUTION:
        case PHASE.SET_MOBILE_NETWORK:
        default:
          return 'secondary';
      }
    },

    /**
     * ダイアログのヘッダー表示文言
     * @returns {string} ヘッダー表示文言
     */
    headerTitle() {
      const confirmTitle = 'message.common_title_confirmation';
      const executeTitle = 'individual.common_title_execution';
      const successTitle = 'message.common_title_success';
      const errorTitle = 'message.common_title_error';

      const titleMap = {
        [PHASE.START]: confirmTitle,
        [PHASE.EXECUTION]: confirmTitle,
        [PHASE.SET_MOBILE_NETWORK]: executeTitle,
        [PHASE.SET_MOBILE_NETWORK_FAILED]: errorTitle,
        [PHASE.FAILED]: errorTitle,
        [PHASE.SUCCESS]: successTitle,
      };

      return this.$t(titleMap[this.phase]);
    },

    /**
     * 作業サポート表示メッセージ
     * @returns {string} メッセージ
     */
    workSupportMessage() {
      const messageMap = {
        [PHASE.START]: "",
        [PHASE.SET_MOBILE_NETWORK]: `${this.i18nWsKey}.REQ_WS_157_TXT_2`,
        [PHASE.SET_MOBILE_NETWORK_FAILED]: `${this.i18nWsKey}.REQ_WS_157_TXT_4`,
        [PHASE.FAILED]: `individual.error_communication_error`,
        [PHASE.SUCCESS]: `${this.i18nWsKey}.REQ_WS_157_TXT_3`,
      };

      if (this.phase === PHASE.EXECUTION) {
        return this.createMessageForExecution();
      }

      return this.$t(messageMap[this.phase]);
    },

    /**
     * [OFF]ボタンの表示判定
     * @returns {boolean} trueなら表示
     */
    showButtonOff() {
      return this.phase === PHASE.EXECUTION;
    },

    /**
     * [ON]ボタンの表示判定
     * @returns {boolean} trueなら表示
     */
    showButtonOn() {
      return this.phase === PHASE.EXECUTION;
    },

    /**
     * [OK]ボタンの表示判定
     * @returns {boolean} trueなら表示
     */
    showButtonOk() {
      const showCancelButtonMap = {
        [PHASE.SET_MOBILE_NETWORK_FAILED]: true,
        [PHASE.FAILED]: true,
        [PHASE.SUCCESS]: true,
      };
      let result = showCancelButtonMap[this.phase];
      if (!result) {
        // 合致しない場合は false とする。
        result = false;
      }
      return result;
    },

    /**
     * [キャンセル]ボタンの表示判定
     * @returns {boolean} trueなら表示
     */
    showButtonCancel() {
      return this.phase === PHASE.EXECUTION;
    },

    /** 
     * [ON]ボタンの活性/非活性判定
     * @returns {boolean} trueなら非活性
     */
    isDisabledOn() {
      return this.isLoading || !this.isOff;
    },

    /** 
     * [OFF]ボタンの活性/非活性判定
     * @returns {boolean} trueなら非活性
     */
    isDisabledOff() {
      return this.isLoading || this.isOff;
    },

    /** 
     * ダイアログの大きさ
     * @returns {string} ダイアログの大きさ
     */
    dialogSize() {
      return this.phase === PHASE.EXECUTION ? "lg" : "md";
    }
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    /** 初期化処理 */
    async init() {
      this.systemId = individualDiagnose.getCurrentSystemId(this);
      this.modal_requirement_157 = true;

      // データ読出し実行
      await this.readData();
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     */
    validateErrorResult(result) {
      apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; this.showUnexpectedError(); },
      );
    },

    /** 
     * 実行画面表示用の文言を作成する
     * @returns {string} 実行画面表示用の文言
     */
    createMessageForExecution() {
      const message = this.$t(`${this.i18nWsKey}.REQ_WS_157_TXT_1`);
      const connectionStatusStr = this.isOff ? this.$t('individual.label_button_off') : this.$t('individual.label_button_on');
      return `${message} ${connectionStatusStr}`;
    },

    /** データ読出し */
    async readData() {
      try {
        this.isLoading = true;

        // バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: API_TYPE.READ,
          },
          // エラー発生のコールバックを実装する。
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (!response) return;
        this.isDispose = response.data.user_data.is_dispose ? response.data.user_data.is_dispose : false;
        switch (response.data.status) {
          // 正常終了
          case REQUEST_STATUS_COMPLETED:
            this.isOff = response.data.user_data.is_off;
            // 実行画面へ遷移
            this.phase = PHASE.EXECUTION;
            break;
          // 異常終了
          case REQUEST_STATUS_FAILED:
            // エラー画面へ遷移
            await this.showError();
            break;
          default:
            // 共通エラー画面へ遷移
            this.showUnexpectedError();
            break;
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** データ書込み */
    async writeData() {
      try {
        this.isLoading = true;

        // バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: API_TYPE.WRITE,
            is_off: this.isOff
          },
          // エラー発生のコールバックを実装する。
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (!response) return;
        switch (response.data.status) {
          // 正常終了
          case REQUEST_STATUS_COMPLETED:
            // 停止画面へ遷移
            await this.showStop();
            break;
          // 異常終了  
          case REQUEST_STATUS_FAILED:
            // エラー画面へ遷移
            await this.showError();
            break;
          default:
            // 共通エラー画面へ遷移
            this.showUnexpectedError();
            break;
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** 終了処理 */
    async exit() {
      try {
        this.isLoading = true;

        // バックエンドAPI POST,GET
        // ここではAPIの実行のみ
        const token = session.getToken();
        await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: API_TYPE.EXIT,
          },
          // エラー発生のコールバックを実装する。
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );
      } finally {
        this.isLoading = false;
      }

    },

    /** Dispose処理 */
    async dispose() {
      try {
        this.isLoading = true;

        // バックエンドAPI POST,GET
        // ここではAPIの実行のみ
        const token = session.getToken();
        await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: API_TYPE.DISPOSE
          },
          // エラー発生のコールバックを実装する。
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );
      } finally {
        this.isLoading = false;
      }
    },

    /** 共通のキャンセルボタン押下処理 */
    async onCancelClicked() {
      // 終了処理判定
      await this.exitProcess();
      this.closeWizard();
    },

    /** 共通のOKボタン押下処理 */
    async onOkClicked() {
      switch (this.phase) {
        case PHASE.SUCCESS:
        case PHASE.SET_MOBILE_NETWORK_FAILED:
          this.closeWizard();
          break;
        case PHASE.FAILED:
          await this.exit();
          this.closeWizard();
          break;
        default:
          break;
      }
    },

    /** 実行画面のON,OFFボタン押下処理 */
    async onExecuteClicked() {
      // モバイルネットワーク設定画面へ遷移
      this.phase = PHASE.SET_MOBILE_NETWORK;
      // データ書込み実行
      await this.writeData();
    },

    /** 終了処理判定(Dispose or Exit) */
    async exitProcess() {
      if (this.isDispose) {
        await this.dispose();
      } else {
        await this.exit();
      }
    },

    /** エラー画面へ遷移する */
    async showError() {
      // 終了処理判定
      await this.exitProcess();
      if (this.phase !== PHASE.FAILED) {
        // 共通エラー以外の場合はエラー画面へ遷移
        this.phase = PHASE.SET_MOBILE_NETWORK_FAILED;
      }
    },

    /** 停止画面へ遷移する */
    async showStop() {
      // 終了処理判定
      await this.exitProcess();
      if (this.phase !== PHASE.FAILED) {
        // 共通エラー以外の場合は停止画面へ遷移
        this.phase = PHASE.SUCCESS;
      }
    },

    /** Wizardを閉じる */
    closeWizard() {
      this.$emit("close-wizard");
      this.modal_requirement_157 = false;
    },

    /** その他エラー発生時の表示 */
    showUnexpectedError() {
      // 共通エラー表示
      this.phase = PHASE.FAILED;
    },
  },

  mounted() {
    this.init();
  },
};